// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import type { CarePlan } from '@medplum/fhirtypes';
import {
  buildTreatmentCarePlan,
  type PatientTreatment,
  TREATMENT_LEGACY_KEY_IDENTIFIER_SYSTEM,
  TREATMENT_ORDER_IDENTIFIER_SYSTEM,
} from '@polarissleep/shared';
import { localDateString } from './patient-treatments';
import { getOrganizationId } from './tenant';

function updateIfUnchanged(medplum: MedplumClient, carePlan: CarePlan): Promise<CarePlan> {
  const versionId = carePlan.meta?.versionId;
  return medplum.updateResource(carePlan, versionId ? { headers: { 'If-Match': `W/"${versionId}"` } } : undefined);
}

/**
 * Creates or updates the treatment CarePlan for one treatment. Pass `existing` to update it in place, or `legacyKey`
 * when the treatment is still only on the legacy Patient extension.
 *
 * A DME or legacy treatment without `existing` is matched on its order-ID or legacy-key identifier first, so a
 * CarePlan the bot or backfill created since the profile loaded is updated instead of duplicated. Updates send
 * If-Match, so a save from a stale copy (say, after the bot voided the treatment) fails instead of overwriting it.
 *
 * Kept apart from `patient-treatments.ts` because `./tenant` reads `import.meta.env`, which one-off scripts lack.
 */
export async function saveTreatmentCarePlan(
  medplum: MedplumClient,
  patientId: string,
  treatment: Omit<PatientTreatment, 'id'>,
  { existing, legacyKey }: { existing?: CarePlan; legacyKey?: string } = {}
): Promise<CarePlan> {
  const build = (current?: CarePlan): CarePlan =>
    buildTreatmentCarePlan({
      treatment,
      patientId,
      orgId: getOrganizationId(),
      existing: current,
      legacyKey,
      today: localDateString(),
    });
  if (existing?.id) {
    return updateIfUnchanged(medplum, build(existing));
  }

  let identifier: string | undefined;
  if (treatment.orderId) {
    identifier = `${TREATMENT_ORDER_IDENTIFIER_SYSTEM}|${treatment.orderId}`;
  } else if (legacyKey) {
    identifier = `${TREATMENT_LEGACY_KEY_IDENTIFIER_SYSTEM}|${legacyKey}`;
  }
  if (!identifier) {
    return medplum.createResource(build());
  }
  const current = await medplum.searchOne('CarePlan', { identifier }, { cache: 'no-cache' });
  if (current) {
    return updateIfUnchanged(medplum, build(current));
  }
  // If another writer creates it between the search and here, If-None-Exist returns theirs rather than a duplicate.
  return medplum.createResourceIfNoneExist(build(), `identifier=${identifier}`);
}
